import { useState } from "react";
import { ChevronLeft, ChevronRight, Eye, FileText, Tag } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";

export function PreviewModal({ variant = "gruender", triggerLabel = "Siehe Vorschau", onCta, ctaLabel = "Jetzt Report erstellen", order, labels }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const pageCount = variant === "business" ? 23 : 21;
  const seq = order && order.length ? order : Array.from({ length: pageCount }, (_, n) => n + 1);
  const images = seq.map((p) => `/preview/${variant}-${p}.png`);
  const total = images.length;
  const currentLabel = labels ? labels[seq[i]] : null;

  const go = (dir) => setI((prev) => (prev + dir + total) % total);

  const title = variant === "business" ? "Vorschau: Gründio-Report (Business)" : "Vorschau: Gründio Report";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setI(0); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`preview-trigger-${variant}`}
          className="group inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 font-semibold text-sm shadow-sm transition-all hover:scale-[1.03] hover:shadow-md"
          style={{ borderColor: "var(--brand-solid)", color: "var(--brand-solid)", background: "var(--brand-tint)" }}
        >
          <Eye className="h-4 w-4" />
          {triggerLabel}
          <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid={`preview-modal-${variant}`}
        className={`max-w-2xl w-[calc(100vw-1.5rem)] p-0 overflow-hidden gap-0 rounded-2xl ${variant === "business" ? "theme-petrol" : ""}`}
      >
        <DialogHeader className="px-5 py-4 border-b border-slate-100">
          <DialogTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Blättere durch alle {pageCount} Seiten des vollständigen PDF-Reports.
          </DialogDescription>
        </DialogHeader>

        {/* Slider */}
        <div className="relative bg-slate-100">
          {currentLabel && (
            <span
              data-testid="preview-page-label"
              className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white shadow-lg brand-gradient-bg animate-fade-in-up"
            >
              <Tag className="h-3.5 w-3.5" /> {currentLabel}
            </span>
          )}
          <div className="max-h-[62vh] overflow-y-auto flex items-start justify-center p-4">
            <img
              key={images[i]}
              src={images[i]}
              alt={`Vorschau Seite ${i + 1}`}
              data-testid={`preview-image-${variant}`}
              className="w-full max-w-md rounded-lg shadow-xl border border-slate-200 animate-fade-in-up"
            />
          </div>

          {/* Prev / Next */}
          <button
            type="button"
            onClick={() => go(-1)}
            data-testid="preview-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/95 shadow-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all"
            aria-label="Vorherige Seite"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            data-testid="preview-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/95 shadow-md border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all"
            aria-label="Nächste Seite"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Progress + counter + CTA */}
        <div className="px-5 py-4 border-t border-slate-100 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full brand-gradient-bg transition-all duration-300"
                style={{ width: `${((i + 1) / total) * 100}%` }}
              />
            </div>
            <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">Seite {i + 1} / {total}</span>
          </div>

          <button
            type="button"
            data-testid={`preview-cta-${variant}`}
            onClick={() => { setOpen(false); onCta && onCta(); }}
            className="w-full h-12 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
          >
            <FileText className="h-5 w-5" />
            {ctaLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
